import { ref, computed, onMounted, onUnmounted } from 'vue'
import axios from 'axios'

export function useReviewSlider(props) {
  /** State */
  const reviews = ref([])
  const track = ref(null)
  const currentSlide = ref(0)
  const itemsPerSlide = ref(3)

  /** Drag state */
  const isDragging = ref(false)
  const startX = ref(0)
  const scrollLeftStart = ref(0)
  const suppressScrollUpdate = ref(false)

  /** Compute how many pages (slides) */
  const totalSlides = computed(() =>
    Math.max(1, Math.ceil(reviews.value.length / itemsPerSlide.value))
  )

  /** Fetch reviews */
  async function fetchReviews() {
    const { data } = await axios.get('/api/v1/reviews', {
      params: { site_id: props.siteId, lang: props.lang, limit: 9 }
    })
    reviews.value = data
  }

  function updateItemsPerSlide() {
    itemsPerSlide.value = window.innerWidth < 768 ? 1 : 3
    currentSlide.value = 0
    if (track.value) track.value.scrollTo({ left: 0 })
  }

  /** Calculate page index based on scroll position */
  function getPageIndex() {
    if (!track.value) return 0
    const maxScroll = track.value.scrollWidth - track.value.clientWidth
    const pages = totalSlides.value
    if (pages <= 1 || maxScroll <= 0) return 0
    const step = maxScroll / (pages - 1)
    return Math.round(track.value.scrollLeft / step)
  }

  /** Handle native scroll to update dot indicator */
  function onScroll() {
    if (suppressScrollUpdate.value) return
    if (!track.value) return
    currentSlide.value = getPageIndex()
  }

  /** Scroll to specific slide (dot click) */
  function scrollToSlide(idx) {
    if (!track.value) return
    suppressScrollUpdate.value = true
    track.value.scrollTo({
      left: idx * track.value.clientWidth,
      behavior: 'smooth'
    })
    currentSlide.value = idx
    setTimeout(() => {
      suppressScrollUpdate.value = false
    }, 400)
  }

  /** Mouse drag handlers */
  // Called on mousedown to start dragging
  function startDrag(e) {
    e.preventDefault()
    if (!track.value) return
    isDragging.value = true
    track.value.classList.add('dragging')
    startX.value = e.pageX - track.value.offsetLeft
    scrollLeftStart.value = track.value.scrollLeft
    suppressScrollUpdate.value = true
    window.addEventListener('mousemove', onDrag)
    window.addEventListener('mouseup', stopDrag)
  }

  // Called on mousemove to perform drag
  function onDrag(e) {
    if (!isDragging.value || !track.value) return
    e.preventDefault()
    const x = e.pageX - track.value.offsetLeft
    const walk = x - startX.value
    track.value.scrollLeft = scrollLeftStart.value - walk
    currentSlide.value = getPageIndex()
  }

  // Called on mouseup to stop dragging
  function stopDrag() {
    if (!track.value) return
    isDragging.value = false
    track.value.classList.remove('dragging')
    window.removeEventListener('mousemove', onDrag)
    window.removeEventListener('mouseup', stopDrag)
    suppressScrollUpdate.value = false
    currentSlide.value = getPageIndex()
  }

  /** Truncate long text */
  function truncate(text, max) {
    return text.length > max ? text.slice(0, max) + '…' : text
  }

  // Lifecycle
  onMounted(async () => {
    await fetchReviews()
    updateItemsPerSlide()
    window.addEventListener('resize', updateItemsPerSlide)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateItemsPerSlide)
  })

  return {
    // State
    reviews,
    track,
    currentSlide,
    itemsPerSlide,
    isDragging,
    startX,
    scrollLeftStart,
    suppressScrollUpdate,

    // Computed
    totalSlides,

    // Methods
    fetchReviews,
    updateItemsPerSlide,
    getPageIndex,
    onScroll,
    scrollToSlide,
    startDrag,
    onDrag,
    stopDrag,
    truncate
  }
}